import React, { useState, useEffect } from 'react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import useUpdateCompany, {
  UpdateCompanyData,
} from 'src/api/core/companies/useUpdateCompany';
import { CompanyDTO } from 'src/api/core/companies/useCompany';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { CURRENCY_CODE_VALUES, CurrencyCode } from '@erp/shared-types';

interface CompanyEditFormProps {
  company: CompanyDTO;
  onSave: () => void;
  onCancel: () => void;
}

const CompanyEditForm: React.FC<CompanyEditFormProps> = ({
  company,
  onSave,
  onCancel,
}) => {
  const [formData, setFormData] = useState<UpdateCompanyData>({
    name: company.name,
    email: company.email || '',
    address: company.primaryAddress
      ? {
          street: company.primaryAddress.addressLine1,
          city: company.primaryAddress.locality,
          state: company.primaryAddress.administrativeArea || '',
          country: company.primaryAddress.countryIso31661Alpha2,
          postalCode: company.primaryAddress.postalCode || '',
        }
      : {
          street: '',
          city: '',
          state: '',
          country: 'US',
          postalCode: '',
        },
    metadata: company.metadata || {},
  });

  // IMPORTANT: Currency handling - temporary solution
  // @see packages/shared-types/src/currency.ts
  const [defaultCurrency, setDefaultCurrency] = useState<CurrencyCode>(() => {
    const stored = company.metadata?.defaultCurrency;
    // Runtime validation: only use if it's a valid currency code
    if (stored && typeof stored === 'string') {
      return stored as CurrencyCode;
    }
    return 'CAD';
  });

  const [defaultTaxRate, setDefaultTaxRate] = useState<string>(() => {
    const stored = company.metadata?.defaultTaxRate;
    if (typeof stored === 'number') {
      return stored.toString();
    }
    return '';
  });

  const [metadataText, setMetadataText] = useState(
    JSON.stringify(company.metadata || {}, null, 2),
  );

  const updateCompanyMutation = useUpdateCompany(company.id, () => {
    onSave();
  });

  // Update form data when company changes
  useEffect(() => {
    setFormData({
      name: company.name,
      email: company.email || '',
      address: company.primaryAddress
        ? {
            street: company.primaryAddress.addressLine1,
            city: company.primaryAddress.locality,
            state: company.primaryAddress.administrativeArea || '',
            country: company.primaryAddress.countryIso31661Alpha2,
            postalCode: company.primaryAddress.postalCode || '',
          }
        : {
            street: '',
            city: '',
            state: '',
            country: 'US',
            postalCode: '',
          },
      metadata: company.metadata || {},
    });
    // Runtime validation for currency
    const stored = company.metadata?.defaultCurrency;
    if (stored && typeof stored === 'string') {
      setDefaultCurrency(stored as CurrencyCode);
    } else {
      setDefaultCurrency('CAD');
    }

    // Tax rate handling
    const storedTaxRate = company.metadata?.defaultTaxRate;
    if (typeof storedTaxRate === 'number') {
      setDefaultTaxRate(storedTaxRate.toString());
    } else {
      setDefaultTaxRate('');
    }

    setMetadataText(JSON.stringify(company.metadata || {}, null, 2));
  }, [company]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Parse metadata JSON
      let parsedMetadata = {};
      if (metadataText.trim()) {
        try {
          parsedMetadata = JSON.parse(metadataText);
        } catch (error) {
          alert('Invalid JSON in metadata field');
          return;
        }
      }

      // Merge defaultCurrency and defaultTaxRate into metadata
      const finalMetadata: Record<string, unknown> = {
        ...parsedMetadata,
        defaultCurrency, // Add/override defaultCurrency in metadata
      };

      // Add defaultTaxRate if valid
      if (defaultTaxRate.trim()) {
        const taxRateNum = parseFloat(defaultTaxRate);
        if (!isNaN(taxRateNum) && taxRateNum >= 0 && taxRateNum <= 100) {
          finalMetadata.defaultTaxRate = taxRateNum;
        }
      }

      const updateData: UpdateCompanyData = {
        name: formData.name,
        email: formData.email || undefined,
        address: formData.address,
        metadata: finalMetadata,
      };

      await updateCompanyMutation.mutateAsync(updateData);
    } catch (error) {
      console.error('Error updating company:', error);
    }
  };

  const isLoading = updateCompanyMutation.isLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Basic Information</h3>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Company Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="Enter company name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email || ''}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              placeholder="Enter email address"
            />
          </div>
        </div>

        {/* Currency Field */}
        <div className="space-y-2">
          <Label htmlFor="defaultCurrency">Default Currency</Label>
          <Select
            value={defaultCurrency}
            onValueChange={(value) => setDefaultCurrency(value as CurrencyCode)}
          >
            <SelectTrigger id="defaultCurrency">
              <SelectValue placeholder="Select default currency" />
            </SelectTrigger>
            <SelectContent>
              {CURRENCY_CODE_VALUES.map((code) => (
                <SelectItem key={code} value={code}>
                  {code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Default currency for invoices from suppliers at this company. Used
            for CSV transformation workflows.
          </p>
        </div>

        {/* Tax Rate Field */}
        <div className="space-y-2">
          <Label htmlFor="defaultTaxRate">Default Tax Rate (%)</Label>
          <Input
            id="defaultTaxRate"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={defaultTaxRate}
            onChange={(e) => setDefaultTaxRate(e.target.value)}
            placeholder="e.g., 13"
          />
          <p className="text-xs text-muted-foreground">
            Default tax rate for supplier payments. Leave blank if not
            applicable. This will pre-populate when recording payments.
          </p>
        </div>
      </div>

      {/* Address Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Address Information</h3>

        <div className="space-y-2">
          <Label htmlFor="street">Street Address</Label>
          <Input
            id="street"
            value={formData.address?.street || ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                address: {
                  ...formData.address,
                  street: e.target.value,
                  city: formData.address?.city || '',
                  state: formData.address?.state || '',
                  country: formData.address?.country || 'US',
                  postalCode: formData.address?.postalCode || '',
                },
              })
            }
            placeholder="Enter street address"
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              value={formData.address?.city || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  address: {
                    ...formData.address,
                    street: formData.address?.street || '',
                    city: e.target.value,
                    state: formData.address?.state || '',
                    country: formData.address?.country || 'US',
                    postalCode: formData.address?.postalCode || '',
                  },
                })
              }
              placeholder="Enter city"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="state">State/Province</Label>
            <Input
              id="state"
              value={formData.address?.state || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  address: {
                    ...formData.address,
                    street: formData.address?.street || '',
                    city: formData.address?.city || '',
                    state: e.target.value,
                    country: formData.address?.country || 'US',
                    postalCode: formData.address?.postalCode || '',
                  },
                })
              }
              placeholder="Enter state"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="postalCode">Postal Code</Label>
            <Input
              id="postalCode"
              value={formData.address?.postalCode || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  address: {
                    ...formData.address,
                    street: formData.address?.street || '',
                    city: formData.address?.city || '',
                    state: formData.address?.state || '',
                    country: formData.address?.country || 'US',
                    postalCode: e.target.value,
                  },
                })
              }
              placeholder="Enter postal code"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="country">Country</Label>
          <select
            id="country"
            value={formData.address?.country || 'US'}
            onChange={(e) =>
              setFormData({
                ...formData,
                address: {
                  ...formData.address,
                  street: formData.address?.street || '',
                  city: formData.address?.city || '',
                  state: formData.address?.state || '',
                  country: e.target.value,
                  postalCode: formData.address?.postalCode || '',
                },
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="MX">Mexico</option>
            <option value="GB">United Kingdom</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="IT">Italy</option>
            <option value="ES">Spain</option>
            <option value="AU">Australia</option>
            <option value="JP">Japan</option>
            <option value="CN">China</option>
            <option value="IN">India</option>
            <option value="BR">Brazil</option>
          </select>
        </div>
      </div>

      {/* Metadata */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Additional Information</h3>

        <div className="space-y-2">
          <Label htmlFor="metadata">Metadata (JSON)</Label>
          <Textarea
            id="metadata"
            value={metadataText}
            onChange={(e) => setMetadataText(e.target.value)}
            placeholder="Enter JSON metadata"
            className="min-h-[120px] font-mono text-sm"
          />
          <p className="text-xs text-gray-500">
            Enter valid JSON for additional company metadata
          </p>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex gap-2 pt-4 border-t">
        <Button type="submit" disabled={isLoading || !formData.name?.trim()}>
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default CompanyEditForm;
